// PERMANENT: Terms of Service — required for AdSense compliance. Do not remove.
// Styled to match PrivacyPolicyPage (topo-bg, "Back to the journal", "Legal" label).
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const NAVY = "#1B2F5E";
const GOLD = "#C9A96E";

export default function TermsPage() {
  const updated = "August 2026";
  return (
    <div className="min-h-screen topo-bg" data-testid="terms-page">
      <SeoHead
        title="Terms of Service"
        description="The terms that govern your use of SummaryMaps Journal — summarymapsjournal.com."
      />
      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-20 sm:pb-24" style={{ maxWidth: 720 }}>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-brand mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to the journal
        </Link>

        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: GOLD }}>Legal</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold leading-tight" style={{ color: NAVY }}>Terms of Service</h1>
        <p className="mt-4 text-sm" style={{ color: "#5C5344" }}>Effective date: {updated}</p>

        <div style={{ color: "#2D3748", fontSize: "1.0625rem", lineHeight: 1.75 }} className="mt-8 space-y-5">
          <p>
            These Terms of Service ("Terms") govern your use of SummaryMaps Journal at
            summarymapsjournal.com (the "Site"), operated by Summary Maps LLC ("we", "us", or
            "Summary Maps"). If you have any questions about these Terms, contact us at{" "}
            <a href="mailto:contact@summarymaps.com" style={{ color: GOLD, textDecoration: "underline" }}>contact@summarymaps.com</a>.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>1. Acceptance of Terms</h2>
          <p>
            By accessing or using the Site, you agree to be bound by these Terms and our Privacy
            Policy. If you do not agree with any part of these Terms, please do not use the Site.
            We may update these Terms from time to time; your continued use of the Site after
            changes are posted constitutes acceptance of the revised Terms.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>2. Use of the Site</h2>
          <p>
            The Site provides editorial content — essays, book summaries, mind mapping guides, and
            educational resources — for personal, non-commercial use. You agree not to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Scrape, reproduce, or redistribute Site content in bulk without written permission.</li>
            <li>Use the Site for any unlawful purpose or in violation of applicable regulations.</li>
            <li>Attempt to interfere with the Site's operation, security, or availability.</li>
            <li>Misrepresent your identity or affiliation when contacting us or subscribing.</li>
          </ul>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>3. Intellectual Property</h2>
          <p>
            All content on the Site — including articles, graphics, logos, and design — is the
            property of Summary Maps LLC or its licensors and is protected by copyright and other
            intellectual property laws. You may share links to our articles and quote brief
            excerpts with attribution. Any other reproduction, distribution, or derivative use
            requires our prior written consent. Book titles, covers, and author names referenced
            on the Site belong to their respective owners and are used for commentary and review.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>4. Newsletter & Communications</h2>
          <p>
            If you subscribe to our newsletter or download our free guides, you consent to receive
            emails from us. You can unsubscribe at any time using the link in any email. We will
            never sell your email address — see our{" "}
            <Link to="/privacy" style={{ color: GOLD, textDecoration: "underline" }}>Privacy Policy</Link>{" "}
            for details.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>5. Disclaimers & Limitation of Liability</h2>
          <p>
            The Site and its content are provided "as is" and "as available" without warranties of
            any kind, express or implied. Our articles are for informational and educational
            purposes only and do not constitute professional advice. To the fullest extent
            permitted by law, Summary Maps LLC shall not be liable for any indirect, incidental,
            consequential, or punitive damages arising from your use of — or inability to use —
            the Site or its content.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>6. Third-Party Links & Affiliate Disclosure</h2>
          <p>
            We do not currently show display advertising on SummaryMaps Journal. The Site links to third-party
            websites (including summarymaps.com), and some book links are affiliate links to Amazon (as an Amazon
            Associate we earn from qualifying purchases) and Bookshop.org. We are not responsible for the content,
            products, or practices of third-party sites. Your interactions with those sites are solely between you
            and them.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>7. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Wyoming, United States, without
            regard to conflict-of-law principles. Any disputes arising from these Terms or your
            use of the Site shall be resolved in the courts located in Wyoming.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>8. Changes to These Terms</h2>
          <p>
            We may revise these Terms at any time by posting an updated version on this page with
            a new effective date. Material changes will be noted on the Site. We encourage you to
            review this page periodically.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>9. Contact Us</h2>
          <p>
            Questions about these Terms? Reach us anytime at{" "}
            <a href="mailto:contact@summarymaps.com" style={{ color: GOLD, textDecoration: "underline" }}>contact@summarymaps.com</a>.
          </p>
        </div>
      </main>
    </div>
  );
}
